// Lead-gate API — calls the experience server's PUBLIC lead functions.
//
// No JWT: the function injects the vr-server-client service token. The contract
// mirrors the meta-model engine's guided flow (opensky-vr-engine src/app.js:
// send-otp / verify-otp + extractOtpProof), plus the gate's check-status
// pre-step. Endpoints:
//   <BASE>/lead/check-status  -> { next_action: unlock | send_otp | collect_name }  (409 = identity conflict)
//   <BASE>/lead/send-otp
//   <BASE>/lead/verify-otp    -> returns customer_id / otp_token
//
// NOTE: the backend currently rejects these with `AuthClaimMissing` until the
// excluded_routes + send_otp fix ships (Nivedita). Until then DEMO_FALLBACK
// lets the full UX run with a canned OTP so this is showable now — the exact
// pattern the engine uses (`offline_dummy`).

// Same-origin by default — recommended path is to add 3 lead proxy functions to
// rc-app's OWN netlify/functions (mirroring the engine's vr.mjs /lead/* routes),
// so this stays on openskyvr.com: no CORS, no external host. Override with
// setLeadApiBase() if we ever point at a different deploy.
// IMPORTANT: do NOT use view.openskyvr.com — that host is stale.
let BASE = "/.netlify/functions/vr";
/** Override the lead-API base (trailing slash trimmed). */
export const setLeadApiBase = (url: string) => {
  BASE = String(url || "").replace(/\/+$/, "") || BASE;
};

/** Demo OTP accepted while the live endpoints are unreachable. */
export const DEMO_OTP = "123456";
/**
 * Flip to false once the live lead endpoints are confirmed working.
 * Now LIVE: the rc-app `netlify/functions/vr.js` proxy injects the
 * vr-server-client service token and forwards to customers/send_otp etc., so
 * real OTP runs end-to-end (needs vr.js deployed to the stage/prod site — local
 * `yarn develop` proxies /.netlify/functions/* to stage.openskyvr.com).
 * Set back to true to fall back to the canned 123456 demo flow.
 */
export const DEMO_FALLBACK = false;

export interface ApiResult {
  ok: boolean;
  status: number;
  data: any;
  error: string;
  demo?: boolean;
}

const ua = () => (typeof navigator !== "undefined" ? navigator.userAgent : "");
const digits = (s: string) => String(s || "").replace(/\D+/g, "");
const normCountry = (cc?: string) => {
  const c = String(cc || "+91").trim();
  return /^\+\d+$/.test(c) ? c : "+91";
};

const call = async (path: string, body: any): Promise<ApiResult> => {
  const url = `${BASE}${path.startsWith("/") ? path : `/${path}`}`;
  let resp: Response;
  try {
    resp = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (e: any) {
    return { ok: false, status: 0, data: null, error: String(e?.message || "Network error") };
  }
  let data: any = null;
  try {
    data = await resp.json();
  } catch {
    data = null;
  }
  const error =
    data?.error?.detail ||
    data?.error?.message ||
    data?.error ||
    data?.message ||
    (resp.ok ? "" : `Request failed (${resp.status})`);
  return { ok: resp.ok, status: resp.status, data, error: String(error || "") };
};

// While DEMO_FALLBACK is on, any non-2xx that ISN'T a 409 identity conflict
// falls through to the demo path — so the full UX runs whether the proxy isn't
// built yet (404), the backend still rejects with AuthClaimMissing (403), or the
// network is down (0). A 409 is a real product outcome, so it's always surfaced.
const demoTakesOver = (r: ApiResult) => DEMO_FALLBACK && !r.ok && r.status !== 409;

export interface LeadCtx {
  agencyId: string;
  deviceId: string;
  sessionId?: string;
  countryCode?: string;
  /** Website embed key. A meta-model embed has no session of ours, so this is how its lead is marked as an embed lead. */
  embedKey?: string;
  /**
   * The visitor's own campaign tags, forwarded by the snippet from the host page.
   *
   * The VR session normally carries these to the CRM. A meta-model embed has no session of
   * ours, and a twin's can fail to start — and then the lead is filed under the agency's
   * default Source/Sub source, so a PAID click reports as organic. Sending them here is the
   * only route that does not depend on a session existing.
   */
  utm?: Record<string, string>;
}

export type NextAction = "unlock" | "send_otp" | "collect_name";

const baseBody = (ctx: LeadCtx, extra: Record<string, any>) => ({
  agency_id: ctx.agencyId,
  country_code: normCountry(ctx.countryCode),
  device_id: ctx.deviceId,
  user_agent: ua(),
  session_id: ctx.sessionId || "",
  ...(ctx.embedKey ? { embed_key: ctx.embedKey } : {}),
  ...(ctx.utm && Object.keys(ctx.utm).length ? { utm: ctx.utm } : {}),
  ...extra,
});

/** Page-1 lookup: tells us whether to unlock outright, just OTP, or collect a name. */
export const checkLeadStatus = async (
  ctx: LeadCtx,
  fields: { mobile: string; email?: string },
): Promise<ApiResult> => {
  const r = await call(
    "/lead/check-status",
    baseBody(ctx, {
      mobile_number: digits(fields.mobile),
      email_id: String(fields.email || "").trim(),
    }),
  );
  if (!demoTakesOver(r)) return r;
  // Demo: treat as a brand-new lead -> collect name, then OTP.
  return { ok: true, status: 200, data: { data: { next_action: "collect_name" } }, error: "", demo: true };
};

export const sendLeadOtp = async (
  ctx: LeadCtx,
  fields: { mobile: string; email?: string; firstName?: string; lastName?: string },
): Promise<ApiResult> => {
  const r = await call(
    "/lead/send-otp",
    baseBody(ctx, {
      mobile_number: digits(fields.mobile),
      email_id: String(fields.email || "").trim(),
      first_name: String(fields.firstName || "").trim(),
      last_name: String(fields.lastName || "").trim(),
    }),
  );
  if (!demoTakesOver(r)) return r;
  return { ok: true, status: 200, data: { data: { otp_sent: true } }, error: "", demo: true };
};

export interface OtpProof {
  token: string;
  verificationId: string;
  customerId: string;
}

/** Port of the engine's extractOtpProof — tolerant of field-name variants. */
export const extractOtpProof = (payload: any): OtpProof => {
  const root = payload?.data ?? payload?.result ?? payload?.response ?? payload ?? {};
  return {
    token: String(root.otp_token || root.otpToken || root.verification_token || "").trim(),
    verificationId: String(root.verification_id || root.verificationId || root.request_id || "").trim(),
    // verify-otp and a trusted-device check-status return the lead as customer{_id}.
    customerId: String(
      root.customer_id || root.customerId || root.lead_id || root.customer?._id || root.customer?.id || "",
    ).trim(),
  };
};

export const verifyLeadOtp = async (
  ctx: LeadCtx,
  fields: { mobile: string; otp: string; firstName?: string; lastName?: string; email?: string },
): Promise<ApiResult> => {
  const r = await call(
    "/lead/verify-otp",
    baseBody(ctx, {
      mobile_number: digits(fields.mobile),
      otp: String(fields.otp || "").trim(),
      first_name: String(fields.firstName || "").trim(),
      last_name: String(fields.lastName || "").trim(),
      email_id: String(fields.email || "").trim(),
    }),
  );
  if (!demoTakesOver(r)) return r;
  // Demo: accept the canned code, mint a throwaway customer id.
  if (digits(fields.otp) === DEMO_OTP) {
    return {
      ok: true,
      status: 200,
      data: { data: { customer_id: `demo_${Date.now().toString(36)}`, verified: true } },
      error: "",
      demo: true,
    };
  }
  return { ok: false, status: 400, data: null, error: `Invalid code — use ${DEMO_OTP} in demo mode.`, demo: true };
};
